import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Collapse,
} from '@mui/material';
import { useAuth } from '../AuthContext';
import { QUOTE_SOP_QUERY, CREATE_QUOTE_SOP_NOTE_MUTATION } from '../graphql/quoteSop';

interface QuoteSopDialogProps {
  open: boolean;
  onClose: () => void;
  // item_type_enum value as the client holds it (lower_snake_case).
  itemType: string;
  itemTypeLabel?: string;
}

// The SOP markdown files are hard-wrapped at ~80 columns, which leaves the
// text bunched on the left of a wide dialog. Join each wrapped continuation
// line back onto the line before it so paragraphs and list items flow to the
// full width. Display only — the stored text is untouched. Code fences,
// headings, table rows, rules, list markers and blank lines are left alone.
function reflowSop(md: string): string {
  const out: string[] = [];
  let inFence = false;
  let canJoin = false;
  for (const line of md.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.startsWith('```')) {
      inFence = !inFence;
      out.push(line);
      canJoin = false;
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }
    if (trimmed === '') {
      out.push('');
      canJoin = false;
      continue;
    }
    const startsBlock = /^(#{1,6}\s|[-*•●○■]\s|\d+[.)]\s|\||-{3,}$|>)/.test(trimmed);
    if (canJoin && !startsBlock) {
      out[out.length - 1] = out[out.length - 1].replace(/\s+$/, '') + ' ' + trimmed;
    } else {
      out.push(line);
      // Headings, table rows and rules never absorb the line after them.
      canJoin = !/^(#{1,6}\s|\||-{3,}$)/.test(trimmed);
    }
  }
  return out.join('\n');
}

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString();
}

// Shows the repo-seeded SOP (read-only) plus this shop's one living
// clarification note. Saving never edits a row: it inserts a new one, so
// "History" is just the older rows (see migration 126).
export default function QuoteSopDialog({ open, onClose, itemType, itemTypeLabel }: QuoteSopDialogProps) {
  const { user } = useAuth();
  const [draft, setDraft] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(QUOTE_SOP_QUERY, {
    variables: { itemType: itemType.toUpperCase(), domainId: user?.domain_id },
    skip: !open || !itemType || !user?.domain_id,
    fetchPolicy: 'network-only',
  });
  const [createNote, { loading: saving }] = useMutation(CREATE_QUOTE_SOP_NOTE_MUTATION);

  const sop = data?.quoteSopByItemType;
  const notes: any[] = data?.allQuoteSopNotes?.nodes || [];
  const current = notes[0] || null;
  const history = notes.slice(1);
  const body = draft ?? current?.body ?? '';
  const dirty = draft !== null && draft !== (current?.body ?? '');

  const handleSave = async () => {
    setError(null);
    try {
      await createNote({
        variables: {
          input: {
            quoteSopNote: {
              domainId: user?.domain_id,
              itemType: itemType.toUpperCase(),
              body: draft ?? '',
              createdBy: user?.id,
              updatedBy: user?.id,
            },
          },
        },
      });
      setDraft(null);
      await refetch();
    } catch (e: any) {
      setError(e.message || 'Failed to save note');
    }
  };

  const handleClose = () => {
    setDraft(null);
    setShowHistory(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={false}
      slotProps={{ paper: { sx: { width: '80vw', maxWidth: 'none', backgroundColor: '#f3f4f6' } } }}>
      <DialogTitle>Quoting SOP{itemTypeLabel ? ` — ${itemTypeLabel}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading && <Typography>Loading…</Typography>}

        {!loading && !sop && (
          <Typography color="text.secondary">
            No SOP has been loaded for this item type yet.
          </Typography>
        )}

        {sop && (
          <>
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
                fontSize: 14,
                m: 0,
                p: 2,
                borderRadius: 1,
                backgroundColor: '#e5e7eb',
                maxHeight: '55vh',
                overflow: 'auto',
              }}
            >
              {reflowSop(sop.sopMarkdown)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Last updated {formatWhen(sop.updatedAt)}
              {sop.sourceCommit ? ` · doc version ${sop.sourceCommit}` : ''}
            </Typography>
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Shop clarification
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Add anything that needs more clarification for the next person quoting this."
            value={body}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
          />
          {current && (
            <Typography variant="caption" color="text.secondary">
              Last edited {formatWhen(current.createdAt)}
              {current.userByCreatedBy?.username ? ` by ${current.userByCreatedBy.username}` : ''}
            </Typography>
          )}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {history.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => setShowHistory((v) => !v)}>
                {showHistory ? 'Hide history' : `History (${history.length})`}
              </Button>
              <Collapse in={showHistory}>
                {history.map((n) => (
                  <Box key={n.id} sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid #e5e7eb' }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatWhen(n.createdAt)}
                      {n.userByCreatedBy?.username ? ` · ${n.userByCreatedBy.username}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {n.body || '(cleared)'}
                    </Typography>
                  </Box>
                ))}
              </Collapse>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={!dirty || saving || !sop}>
          {saving ? 'Saving…' : 'Save note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
